const Loading = require('./CH2Loading');
const Instructions = require('./CH2Instructions');
const Configurator = require('./CH2Configurator');
const utils = require('./CH2Utilities');
const Analytics = require('./CH2Analytics');

class CH2Start {
    constructor(_window, _pc) {
        this.pc = _pc;
        this.pc.script.legacy = false;
        this.window = _window;
        // PlayCanvas Versioning
        this.pcVersion = _pc.version;
        console.log(`CH2Start ${this.pcVersion}`);

        // HTML Containers
        this.applicationContainerID = 'ch2_application-container';
        this.applicationLoadingContainerID = 'ch2_application-splash-loader';
        this.applicationParentContainerID = 'ch2_player3d-modal';
        this.canvasID = 'ch2_application-canvas';
        this.applicationContainer = null;
        this.applicationLoadingContainer = null;
        this.applicationParentContainer = null;
        this.canvas = null;
        // Internal Vars
        this.isInitialized = false;
        this.devices = null;
        this.app = null;
        this.fillModeNone = true;
        this.camera = null; // ADA + Zoom
        this.orbitCamera = null; // ADA + Zoom
        this.isCameraOrthographic = true;
        this.isProcessingScreenshot = false;
        this.loading = null;
        // Configurator
        this.productConfigurator = null;
        this.productData = null;
        this.shouldOpenConfigurator = false;
        // Buttons
        this.buttonScreenshot = null;
        this.buttonsContainer = null;
        this.instructionButton = null;
        // Analytics
        this.analytics = null;
        this.navigator = null;
        // Max Zoom
        this.zoomStartPdp = -1;
        this.zoomStartConfigurator = -1;
        // ADA
        this.model3D = null;
        this.startDistance = null;
        this.startYaw = null;
        this.startPitch = null;
        this.startPivotPosition = null;
        this.startTargetRotation = null;
        this.orbitSensitivity = 1;
        this.distanceSensitivity = 0.25;
    }

    /**
     * Gets or creates an HTML canvas element for the application.
     * If a canvas element with the specified ID does not exist, a new canvas element is created
     * and added to the application container.
     */
    getCanvas() {
        this.applicationContainer = document.getElementById(
            this.applicationContainerID,
        );
        if (!this.applicationContainer) {
            console.error(
                "Change2: HTML Element with ID='application' not found.",
            );
            this.analytics.logBulgariError("Change2: HTML Element with ID='application' not found.");
        }
        this.canvas = document.getElementById(this.canvasID);
        if (!this.canvas) {
            this.canvas = document.createElement('canvas');
            this.canvas.id = this.canvasID;
            this.canvas.setAttribute('tabindex', -1);
            this.canvas.style['-webkit-user-select'] = 'none'; // -> Disable Long-Touch on iOS (UPDATE)
            this.canvas.onselectstart = function () {
                return false;
            }; // Disable I-Bar cursor on click+drag
            this.applicationContainer.appendChild(this.canvas);
        }
    }

    /**
     * Creates an input devices object for PlayCanvas using the specified canvas, PlayCanvas object, and window object.
     *
     * @param {Html} canvas - The HTML element canvas with id: ch2_application-canvas.
     * @param {PlayCanvas} pc - The PlayCanvas object.
     * @param {Window} window - The window object.
     */
    createInputDevices(canvas, pc, window) {
        const inputSettings = window.INPUT_SETTINGS;
        if (inputSettings) {
            const {
                useMouse,
                useTouch,
                useKeyboard,
                useGamepads,
            } = inputSettings;
            this.devices = {
                elementInput: new pc.ElementInput(canvas, { useMouse, useTouch }),
                keyboard: useKeyboard ? new pc.Keyboard(window) : null,
                mouse: useMouse ? new pc.Mouse(canvas) : null,
                gamepads: useGamepads ? new pc.GamePads() : null,
                touch: useTouch && pc.platform.touch ? new pc.TouchDevice(canvas) : null,
            };
        } else {
            console.error('Change2: Window.INPUT_SETTINGS not found.');
            this.analytics.logBulgariError('Change2: Window.INPUT_SETTINGS not found.');
        }
    }

    /**
     * Takes a screenshot of the PlayCanvas app and downloads it as a PNG file.
     *
     * @param {PlayCanvasApp} app - The PlayCanvas "App" instance.
     * @param {Window} window - The window object.
     */
    takeScreenshot(app, window) {
        // Log a screenshot event to the analytics service
        this.analytics.logEvent('screenshot', null, null);
        // If the screenshot is already being processed, return
        if (this.isProcessingScreenshot) {
            console.warn('Screenshot already in progress.');
            return;
        }
        // If the app is not yet initialized, log an error and return
        if (!app || !app.graphicsDevice) {
            console.error('Cannot take screenshot: app not initialized.');
            this.analytics.logBulgariError('Cannot take screenshot: app not initialized.');
            return;
        }
        // Set the flag indicating that the screenshot is being processed
        this.isProcessingScreenshot = true;

        // Add a postrender event listener to the app
        app.once('postrender', function () {
            // If the browser is Internet Explorer 11, download the image using msToBlob
            if (app.graphicsDevice.canvas.msToBlob) {
                const blob = app.graphicsDevice.canvas.msToBlob();
                window.navigator.msSaveBlob(blob, 'product.png');
            } else {
                // Otherwise, download the image using a data URL and a link element
                const imgCanvasURL = app.graphicsDevice.canvas.toDataURL('image/png');
                const a = document.createElement('a');
                window.document.body.appendChild(a);
                a.setAttribute('download', 'product.png');
                a.setAttribute('href', imgCanvasURL);
                a.click();
                window.document.body.removeChild(a);
            }
            // Set the flag indicating that the screenshot has finished processing
            this.isProcessingScreenshot = false;
        }.bind(this));

        if (!app.autoRender) {
            app.fire('render:frame'); // Force Jewel's shader render event -> Causing a standard postrender event
        }
    }

    /**
     * Display Error - Load Playcanvas
     * @param {Html} message - Error Message
     * @param {Html} container - Div Containing the Error Message
     */
    displayError(message, container) {
        const div = document.createElement('div');
        div.setAttribute('id', 'ch2_error-message');
        div.innerHTML = [
            '<table style="color:#171a1c; background-color: #f2f2f2; width: 100%; height: 100%;">',
            '  <tr>',
            '      <td align="center">',
            '          <div style="display: table-cell; vertical-align: middle;">',
            `              <div>${message}</div>`,
            '          </div>',
            '      </td>',
            '  </tr>',
            '</table>',
        ].join('\n');
        container.appendChild(div);
    }

    /**
     * Initializes and runs the PlayCanvas application.
     */
    runPlaycanvas() {
        if (!this.pc || !this.window.CONTEXT_OPTIONS || !this.window.SCRIPTS) {
            console.error('Change2: Application Critical Objs not found.');
            this.analytics.logBulgariError('Change2: Application Critical Objs not found.');
        }
        try {
            this.applicationParentContainer = document.getElementById(
                this.applicationParentContainerID,
            );
            this.applicationParentContainer.classList.add('ch2_is-open');
            this.applicationLoadingContainer = document.getElementById(
                this.applicationLoadingContainerID,
            );
            this.app = new this.pc.Application(this.canvas, {
                elementInput: this.devices.elementInput,
                keyboard: this.devices.keyboard,
                mouse: this.devices.mouse,
                gamepads: this.devices.gamepads,
                touch: this.devices.touch,
                graphicsDeviceOptions: this.window.CONTEXT_OPTIONS,
                assetPrefix: this.window.ASSET_PREFIX || '',
                scriptPrefix: this.window.SCRIPT_PREFIX || '',
                scriptsOrder: this.window.SCRIPTS || [],
            });
            if (this.fillModeNone) {
                this.app.setCanvasFillMode(this.pc.FILLMODE_NONE);
            }
            this.addEventListeners(this.canvas, this.app);
            this.loading = new Loading(
                this.app,
                this.applicationLoadingContainer,
                this.window
            );
            this.loading.start();
        } catch (errorPc) {
            console.error(
                `Change2: There was an error loading 3D Engine ${errorPc}`,
            );
            this.analytics.logBulgariError(`Change2: There was an error loading 3D Engine ${errorPc}`);
            let message = `Could not initialize application. Error: ${errorPc}`;
            const pc = this.pc;
            if (errorPc instanceof pc.UnsupportedBrowserError) {
                message =
                    'This page requires a browser that supports WebGL.<br/>' +
                    '<a href="http://get.webgl.org">Click here to find out more.</a>';
            } else if (errorPc instanceof pc.ContextCreationError) {
                message =
                    `It doesn't appear your computer can support WebGL.<br/>` +
                    '<a href="http://get.webgl.org/troubleshooting/">Click here for more information.</a>';
            }
            this.displayError(message, this.canvas);
        }
    }

    /**
     * Opens the product configurator.
     * @param {Object} productData - Product data
     */
    openConfigurator(productData) {
        // If the product is not configurable, do nothing
        if (!productData || !productData.isConfigurable) {
            return;
        }
        // If the application is not initialized, save the product data
        // and set a flag to open the configurator when the application is initialized.
        if (!this.isInitialized) {
            this.productData = productData;
            this.shouldOpenConfigurator = true;
            this.run();
            return;
        }
        // Create a new configurator and run it
        const config = new Configurator(this.window, this.pc, this.app, productData);
        this.productConfigurator = config;
        this.productConfigurator.run();
        this.analytics.logEvent('open_configurator', null, null);
        this.window.document.body.classList.add('ch2_noscroll');
        this.set3dVisualMode(true);
        this.initDropDown();
    }

    /**
     * Configure Playcanvas Application
     * @param {PlayCanvasApp} app - PlayCanvas "App" instance
     * @param {Window} window - Window Object
     */
    configure(app, window) {
        if (!window.CONFIG_FILENAME || !window.SCENE_PATH) {
            console.error('Start3D_ConfigureError');
            this.analytics.logBulgariError('Start3D_ConfigureError');
        }
        // On Start Events
        app.on(
            'start',
            function () {
                if (this.shouldOpenConfigurator) {
                    this.openConfigurator(this.productData);
                }
            }.bind(this),
        );

        app.once('postrender', this.setCameraAndADA.bind(this));

        // Configure
        app.configure(window.CONFIG_FILENAME, function (configErr) {
            if (configErr) {
                console.error(`Start3D_ConfigureError ${configErr}`);
                this.analytics.logBulgariError(`Start3D_ConfigureError ${configErr}`);
            }
            setTimeout(function () {
                utils.reflow(this.canvas, app);
                app.preload(function (preloadErr) {
                    if (preloadErr) {
                        console.error(`Start3D_PreloaSceneError ${preloadErr}`);
                        this.analytics.logBulgariError(`Start3D_PreloaSceneError ${preloadErr}`);
                    }
                    if (this.pcVersion === '1.52.6') {
                        console.log('Legacy 1.52.6 - Configure');
                        app.loadScene(
                            window.SCENE_PATH,
                            function (loadErr, scene) {
                                if (loadErr) {
                                    console.error(
                                        `Start3D_LoadSceneError ${loadErr}`,
                                    );
                                    this.analytics.logBulgariError(`Start3D_LoadSceneError ${loadErr}`);
                                }
                                if (scene) {
                                    app.start();
                                }
                            },
                        );
                    } else if (this.pcVersion === '1.65.3') {
                        console.log(`LSR ${this.pcVersion} - Configure`);
                        app.scenes.loadScene(
                            window.SCENE_PATH,
                            function (err, scene) {
                                if (err) {
                                    console.error(err);
                                    this.analytics.logBulgariError(`Start3D_LoadSceneError ${err}`);
                                }
                                if (scene) {
                                    app.start();
                                }
                            },
                        );
                    }
                }.bind(this));
            }.bind(this));
        }.bind(this));
    }

    /**
     * Sets the camera type, camera limits, and ADA compliancy.
     */
    setCameraAndADA() {
        this.recognizeCameraType();
        this.setCameraLimits();
        this.setADA();
    }

    /**
     * Add Event Listeners for Resize and Orientation Change - Reflow
     * @param {Html} canvas - HTML Element Canvas with id: ch2_application-canvas
     * @param {PlayCanvasApp} app - PlayCanvas "App" instance
     */
    addEventListeners(canvas, app) {
        this.window.addEventListener(
            'resize',
            utils.reflow(canvas, app),
            false,
        );
        this.window.addEventListener(
            'orientationchange',
            utils.reflow(canvas, app),
            false,
        );
    }

    /**
     * Sets up a 3D model viewer with interactive controls for zooming, rotating, and resetting the view.
     * Initializes variables for the initial camera distance, yaw, pitch, pivot point, and target object rotation.
     * Defines several functions for performing various actions, including resetting the view, zooming in/out, and rotating.
     * Creates a controller object to map keyboard keys to these functions.
     * Defines event handlers for keydown and keyup events to update the pressed flag of the controller object based on keyboard input.
     * Defines a moveModel() function to iterate through the controller object and call the corresponding function for any pressed keys.
     * Adds moveModel() as a listener for the 'update' event of the app object, and adds handleKeyDown() and handleKeyUp() as event listeners for keydown and keyup events on the document object of the window.
     */
    setADA() {
        this.model3D = this.app.root.findByName('3D_Model');
        this.startDistance = this.orbitCamera.distance;
        this.startYaw = this.orbitCamera.yaw;
        this.startPitch = this.orbitCamera.pitch;
        this.startPivotPosition = this.orbitCamera.pivotPoint.clone();
        this.startTargetRotation = this.model3D.getRotation().clone();

        const controller = {
            32: { pressed: false, func: function () { this.resetModel(); }.bind(this) },
            107: { pressed: false, func: function () { this.zoom(true, this.isCameraOrthographic, true); }.bind(this) }, // zoomIn
            109: { pressed: false, func: function () { this.zoom(false, this.isCameraOrthographic, true); }.bind(this) }, // zoomOut
            37: { pressed: false, func: function () { this.rotateObject(-this.orbitSensitivity, 0); }.bind(this) }, // rotateLeft
            39: { pressed: false, func: function () { this.rotateObject(this.orbitSensitivity, 0); }.bind(this) }, // rotateRight
            38: { pressed: false, func: function () { this.rotateObject(0, -this.orbitSensitivity); }.bind(this) }, // rotateUp
            40: { pressed: false, func: function () { this.rotateObject(0, this.orbitSensitivity); }.bind(this) }, // rotateDown
            187: { pressed: false, func: function () { this.zoom(true, this.isCameraOrthographic, true); }.bind(this) }, // zoomIn
            189: { pressed: false, func: function () { this.zoom(false, this.isCameraOrthographic, true); }.bind(this) }, // zoomOut
        };

        function handleKeyDown(e) {
            if (controller[e.keyCode]) controller[e.keyCode].pressed = true;
        }

        function handleKeyUp(e) {
            if (controller[e.keyCode]) controller[e.keyCode].pressed = false;
        }

        function moveModel() {
            Object.keys(controller).forEach((key) => {
                if (controller[key].pressed) controller[key].func();
            });
        }

        this.app.on('update', moveModel);
        this.window.document.addEventListener('keydown', handleKeyDown);
        this.window.document.addEventListener('keyup', handleKeyUp);
    }

    /**
     * Resets the model to its initial state.
     */
    resetModel() {
        this.orbitCamera.reset(this.startYaw, this.startPitch, this.startDistance);
        this.orbitCamera.pivotPoint = this.startPivotPosition;
        this.model3D.setRotation(this.startTargetRotation);
    }

    /**
     * Rotates the object by a specified amount.
     * @param {number} dx - The amount to rotate around the y-axis (in radians).
     * @param {number} dy - The amount to rotate around the x-axis (in radians).
     */
    rotateObject(dx, dy) {
        this.orbitCamera.pitch -= dy * this.orbitSensitivity;
        this.orbitCamera.yaw -= dx * this.orbitSensitivity;
        // Perfumes - Timer Reset
        if (this.camera.script.cameraController) {
            this.camera.script.cameraController.secsSinceUserInput = 0;
        }
    }

    /**
     * Recognize Camera for Zoom and ADA
     */
    recognizeCameraType() {
        const camera = this.app.root.findByName('Camera');
        if (!camera) return;
        this.camera = camera;
        this.orbitCamera = camera.script.orbitCamera;
        this.isCameraOrthographic = this.orbitCamera && camera.camera.projection === 1;
    }

    /**
     * Set the camera zoom limits based on the initial zoom level
     */
    setCameraLimits() {
        // Check if the initial zoom levels are valid
        if (this.zoomStartConfigurator < 0 || this.zoomStartPdp < 0) {
            return;
        }
        // Set the camera distance based on the current mode (configurator or pdp)
        if (!this.isCameraOrthographic) {
            if (this.camera) {
                // const orbitCamera = this.camera.script.orbitCamera;
                if (this.productConfigurator) {
                    // Use the configurator zoom level
                    this.orbitCamera.distance = this.zoomStartConfigurator;
                } else {
                    // Use the pdp zoom level
                    this.orbitCamera.distance = this.zoomStartPdp;
                }
            }
        }
    }

    /**
     * Get the default zoom levels from a configuration file.
     * @param {string} pathAsset - The path of the asset containing the configuration file.
     */
    getDefaultZoom(pathAsset) {
        const configSettingsUrl = `${pathAsset}configurator_settings.json`;
        // Load the configuration file and set the default zoom levels.
        $.getJSON(configSettingsUrl, function (data) {
            if (data.zoomLevels && data.zoomLevels.length > 0) {
                for (let i = 0; i < data.zoomLevels.length; i++) {
                    if (data.zoomLevels[i].PDP) {
                        // Set the default zoom level for the PDP (Product Detail Page).
                        this.zoomStartPdp = data.zoomLevels[i].PDP;
                    } else {
                        // Set the default zoom level for the configurator.
                        this.zoomStartConfigurator = data.zoomLevels[i].Configurator;
                    }
                }
            }
        }.bind(this)).fail(function () {
            console.log('Failed to get configurator_settings.json - Default Zoom');
        });
    }

    /**
     * Zooms the camera in or out, depending on the `zoomIn` parameter.
     * If the camera is orthographic, it uses fixed zoom values.
     * @param {boolean} zoomIn - If true, zoom in; if false, zoom out
     * @param {boolean} fromADA - If true, zoom is called from ADA
     */
    zoom(zoomIn, fromADA = false) {
        if (!this.app) {
            return;
        }
        let currentDistance = 0;
        if (!this.isCameraOrthographic) {
            // Perfumes - Timer Reset
            if (this.camera.script.cameraController) {
                this.camera.script.cameraController.secsSinceUserInput = 0;
            }
            console.log('Orbit Camera');
            currentDistance = this.orbitCamera.distance;
            console.log('currentDistance', currentDistance);
            console.log('DistanceMin', this.orbitCamera.__attributes.distanceMin);
            console.log('DistanceMax', this.orbitCamera.__attributes.distanceMax);
            const zoomLevel = fromADA
                ? (this.orbitCamera.distance * 0.1) * this.distanceSensitivity
                : (this.orbitCamera.__attributes.distanceMax - this.orbitCamera.__attributes.distanceMin) / 4;
            if (zoomIn) {
                if (currentDistance - zoomLevel > this.orbitCamera.__attributes.distanceMin) {
                    this.orbitCamera.distance = currentDistance - zoomLevel;
                } else {
                    this.orbitCamera.distance = this.orbitCamera.__attributes.distanceMin;
                }
            } else {
                if (currentDistance + zoomLevel < this.orbitCamera.__attributes.distanceMax) {
                    this.orbitCamera.distance = currentDistance + zoomLevel;
                } else {
                    this.orbitCamera.distance = this.orbitCamera.__attributes.distanceMax;
                }
            }
            console.log('Distance: ', this.orbitCamera.distance);
        } else {
            console.log('Orthographic Camera');

            currentDistance = this.camera.camera.orthoHeight;

            const max = this.orbitCamera.__attributes.orthograpicMax || 3.0;
            const min = this.orbitCamera.__attributes.orthograpicMin || 0.2;

            const zoomLevel = fromADA
                ? (currentDistance * 0.1) * this.distanceSensitivity
                : 0.7;

            if (zoomIn) {
                if (currentDistance - zoomLevel > min) {
                    this.camera.camera.orthoHeight =
                        currentDistance - zoomLevel;
                } else {
                    this.camera.camera.orthoHeight = min;
                }
            } else {
                if (currentDistance + zoomLevel < max) {
                    this.camera.camera.orthoHeight =
                        currentDistance + zoomLevel;
                } else {
                    this.camera.camera.orthoHeight = max;
                }
            }
            console.log('OrthoHeight: ', this.camera.camera.orthoHeight);
        }
    }

    /**
     * Creates buttons for taking a screenshot, zooming in and out, entering and exiting fullscreen mode, and opening instructions,
     * and appends them to the specified HTML container.
     * @param {HTMLElement} container - The container to which buttons will be appended.
     */
    createButtons(container) {
        // 1. Screenshot Button
        const buttonScreenshot = document.createElement('button');
        buttonScreenshot.id = 'ch2_screenshot-button';
        buttonScreenshot.setAttribute(
            'aria-label',
            this.window.Resource.msg.configuratorScreenshot,
        );
        buttonScreenshot.addEventListener('click', function () {
            this.takeScreenshot(this.app, this.window);
        }.bind(this));
        buttonScreenshot.innerHTML = `<svg height="41" viewBox="0 0 41 41" width="41" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd" stroke="currentColor"><path d="m25.125 20.5781c0 2.554-2.074 4.624-4.626 4.624-2.554 0-4.624-2.07-4.624-4.624s2.07-4.626 4.624-4.626c2.552 0 4.626 2.072 4.626 4.626z" stroke-linejoin="round"/><path d="m26.9746 12.9229h5.396c1.398 0 2.543 1.399 2.543 2.798v11.561c0 1.399-1.145 2.543-2.543 2.543h-23.739c-1.401 0-2.544-1.144-2.544-2.543v-11.561c0-1.399 1.143-2.798 2.544-2.798h5.393" stroke-linecap="round" stroke-linejoin="round"/><path d="m14.0254 12.9229 3.702-3.445h5.546l3.702 3.445" stroke-linecap="round" stroke-linejoin="round"/><path d="m30.6738 16.8789c-.512 0-.926.412-.926.923 0 .514.414.926.926.926s.926-.412.926-.926c0-.511-.414-.923-.926-.923"/><g stroke-linejoin="round"><path d="m1 10.75v-9.75h9.75"/><path d="m10.75 40h-9.75v-9.75"/><path d="m40 30.25v9.75h-9.75"/><path d="m30.25 1h9.75v9.75"/></g></g></svg>`;
        // Buttons Container
        const buttonsContainer = document.createElement('div');
        buttonsContainer.id = 'ch2_btns-container';
        buttonsContainer.classList.add('ch2_btns-container');
        // 2. Zoom In
        const zoomInButton = document.createElement('button');
        zoomInButton.innerHTML = `<svg height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg"><path d="m10.5 1v19m9.5-9.5h-19" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        zoomInButton.id = 'ch2_zoomin-button';
        zoomInButton.addEventListener('click', function () {
            this.zoom(true);
        }.bind(this));
        zoomInButton.setAttribute(
            'aria-label',
            this.window.Resource.msg.configuratorZoomIn,
        );
        // 3. Zoom Out
        const zoomOutButton = document.createElement('button');
        zoomOutButton.innerHTML = `<svg height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg"><path d="m20 10.5h-19" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        zoomOutButton.id = 'ch2_zoomout-button';
        zoomOutButton.addEventListener('click', function () {
            this.zoom(false);
        }.bind(this));
        zoomOutButton.setAttribute(
            'aria-label',
            this.window.Resource.msg.configuratorZoomOut,
        );
        // 4. Open Fullscreen
        const fullscreenButton = document.createElement('button');
        fullscreenButton.innerHTML = `<svg height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="m13.3907 7.6085 6.609-6.608"/><path d="m1.0001 20.0001 6.608-6.608"/><path d="m5.13 20.0001h-4.13v-4.131"/><path d="m20.0001 5.131v-4.131h-4.131"/><path d="m13.3907 13.3917 6.609 6.608"/><path d="m1.0001 1.0001 6.608 6.608"/><path d="m1.0001 5.131v-4.131h4.13"/><path d="m15.8692 20.0001h4.131v-4.131"/></g></svg>`;
        fullscreenButton.id = 'ch2_fullscreen-button';
        fullscreenButton.addEventListener('click', function () {
            this.analytics.logEvent('fullscreen', null, null);
            utils.triggerFullScreen(
                true,
                this.applicationParentContainer,
                this.canvas,
                this.app,
            );
        }.bind(this));
        fullscreenButton.setAttribute(
            'aria-label',
            this.window.Resource.msg.configuratorOpenFullScreen,
        );
        // 5. Close Fullscreen
        const closeFullscreenButton = document.createElement('button');
        closeFullscreenButton.innerHTML = `<svg height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="m7.608 13.3916-6.608 6.608"/><path d="m3.4771 13.3916h4.131v4.13"/><path d="m19.9996 1-6.609 6.608"/><path d="m13.3902 3.4785v4.13h4.131"/><path d="m7.608 7.6084-6.608-6.608"/><path d="m7.608 3.4785v4.13h-4.131"/><path d="m19.9996 20-6.609-6.608"/><path d="m17.5211 13.3916h-4.131v4.13"/></g></svg>`;
        closeFullscreenButton.id = 'ch2_exit-fullscreen-button';
        closeFullscreenButton.addEventListener('click', function () {
            this.analytics.logEvent('exitfullscreen', null, null);
            utils.triggerFullScreen(
                false,
                this.applicationParentContainer,
                this.canvas,
                this.app,
            );
        }.bind(this));
        closeFullscreenButton.setAttribute(
            'aria-label',
            this.window.Resource.msg.configuratorExitFullScreen,
        );
        // Appending
        buttonsContainer.appendChild(zoomInButton);
        buttonsContainer.appendChild(zoomOutButton);
        buttonsContainer.appendChild(fullscreenButton);
        buttonsContainer.appendChild(closeFullscreenButton);
        // 6. Instructions
        const instructionButton = document.createElement('button');
        instructionButton.id = 'ch2_instruction-button';
        instructionButton.innerHTML =
            '<svg role="img" viewBox="0 0 25 25" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g stroke="currentColor"><path d="m24 12.5c0 6.352-5.149 11.5-11.5 11.5-6.352 0-11.5-5.148-11.5-11.5 0-6.351 5.148-11.5 11.5-11.5 6.351 0 11.5 5.149 11.5 11.5z" /><path d="m12.5 17.5v-7.579h-2.526" stroke-linecap="round" stroke-linejoin="round" /><path d="m9.9736 17.5h5.053" stroke-linecap="round" stroke-linejoin="round" /></g><path d="m12.5 6.1318c0 .349-.282.631-.632.631-.349 0-.632-.282-.632-.631 0-.35.283-.632.632-.632.35 0 .632.282.632.632" fill="currentColor" /><path d="m12.5 6.1318c0 .349-.282.631-.632.631-.349 0-.632-.282-.632-.631 0-.35.283-.632.632-.632.35 0 .632.282.632.632z" stroke="currentColor" stroke-linejoin="round" /></g></svg>';
        instructionButton.addEventListener('click', function () {
            this.openInstruction();
        }.bind(this));
        this.instructionButton = instructionButton;
        this.buttonScreenshot = buttonScreenshot;
        this.buttonsContainer = buttonsContainer;
        container.appendChild(buttonScreenshot);
        container.appendChild(buttonsContainer);
        container.appendChild(instructionButton);
    }

    /**
     * Open Instruction Interface
     */
    openInstruction() {
        const instruction = new Instructions();
        instruction.createInstructionInterface(
            this.applicationContainer,
            this.window,
            this.navigator,
        );
        this.analytics.logEvent('instructions', null, null);
    }

    /**
     * Main Function to load assets and run playcanvas
     */
    run() {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;

        if (!this.analytics) {
            this.analytics = new Analytics();
        }
        this.analytics.logEvent('open_viewer', null, null);
        this.navigator = navigator;
        this.getCanvas();
        this.getDefaultZoom(this.window.ASSET_PREFIX);
        this.createInputDevices(this.canvas, this.pc, this.window);
        this.runPlaycanvas(this.canvas);
        this.createButtons(this.applicationContainer);
        this.set3dVisualMode(false);
        if (
            this.window.PRELOAD_MODULES &&
            this.window.PRELOAD_MODULES.length !== 0
        ) {
            // Load Modules
            if (this.pcVersion === '1.52.6') {
                utils.loadModules(
                    this.window.PRELOAD_MODULES,
                    this.window.ASSET_PREFIX,
                    function () {
                        this.configure(this.app, this.window);
                    },
                );
            } else if (this.pcVersion === '1.65.3') {
                utils.loadModules_64(
                    this.pc,
                    this.window.PRELOAD_MODULES,
                    this.window.ASSET_PREFIX,
                    function () {
                        this.configure(this.app, this.window);
                    },
                );
            }
        } else {
            this.configure(this.app, this.window);
        }
    }

    /**
     * Function to switch between Configurator and 3D Viewer
     * @param {Bool} isConfigurator - True: Configurator, False: 3D Viewer.
     */
    set3dVisualMode(isConfigurator) {
        this.applicationParentContainer.classList.remove(
            'mode-configurator-full',
        );
        this.applicationParentContainer.classList.remove('mode-viewer-small');
        const fullscreenButton = document.getElementById(
            'ch2_fullscreen-button',
        );
        if (isConfigurator) {
            this.applicationParentContainer.classList.add(
                'mode-configurator-full',
            );
            fullscreenButton.classList.add('ch2_hidden');
        } else {
            this.applicationParentContainer.classList.add('mode-viewer-small');
            fullscreenButton.classList.remove('ch2_hidden');
        }
    }

    /**
     * Initializes the dropdowns and their change event listeners.
     * - Sets the color change event to call productConfigurator's hasChangedColor function.
     * - Sets the character change event to call productConfigurator's hasChangedText function.
     * - Sets the text position change event to call productConfigurator's hasChangedTextPosition function.
     * - Sets the checkbox change event to call productConfigurator's hasChangedText function with an empty string or a period, depending on the checkbox state.
     * - Sets the click event for the reset button to call configuratorReset.
     */
    initDropDown() {
        $('.ch2_color-dropdown')
            .dropdown()
            .change(
                function (e) {
                    this.productConfigurator.hasChangedColor(e.target.value);
                }.bind(this),
            );
        $('.ch2_char-dropdown')
            .dropdown({ clearable: 1 })
            .change(
                function (e) {
                    this.productConfigurator.hasChangedText(
                        e.target.value,
                        (parseInt(e.target.id.charAt(0), 10) - 1) * 2,
                    );
                    if (this.productConfigurator.numberOfFields === 3) {
                        if (e.target.value !== '') {
                            const elemsCheck = $(`.ch2_bulgari-value_control[value!=${e.target.value}]`);
                            // const elemsCheck = $('.ch2_bulgari-value_control[value!=' + e.target.value + ']');
                            if (elemsCheck.length === 1) {
                                const elementToFind = `.ch2_font_default[data-value="${e.target.value}"]`;
                                elemsCheck.parent().find(elementToFind).addClass('ch2_hidden');
                            }
                        }
                        const elemsCheckHidden = $('.ch2_font_default.ch2_hidden');
                        for (let yy = 0; yy < elemsCheckHidden.length; yy++) {
                            const dataValue = $(elemsCheckHidden[yy]).data('value');
                            const elemsCheck = $(`.ch2_bulgari-value_control[value!=${dataValue}]`);
                            if (elemsCheck.length > 1) {
                                $(elemsCheckHidden[yy]).removeClass('ch2_hidden');
                            }
                        }
                    }
                }.bind(this),
            );
        $('input:checkbox.ch2_feed-stock-input').change(
            function (e) {
                let pointStr = '';
                if (e.target.checked) {
                    pointStr = '.';
                }
                this.productConfigurator.hasChangedText(
                    pointStr,
                    (parseInt(e.target.id.charAt(0), 10) - 1) * 2 + 1,
                );
            }.bind(this),
        );
        $('.ch2_text-position-dropdown')
            .dropdown()
            .change(
                function (e) {
                    this.productConfigurator.hasChangedTextPosition(
                        e.target.value,
                    );
                }.bind(this),
            );

        document.getElementById('ch2_configurator-reset').onclick =
            function () {
                this.configuratorReset();
                if (this.analytics) {
                    this.analytics.logBvlgariEvent('configuration_reset', null, null);
                }
            }.bind(this);
    }

    /**
     * Reset configurator.
     * Resets all configuration options to their default values and empties out any entered text.
     */
    configuratorReset() {
        const ch2FeedCheckbox = document.querySelectorAll(
            '.ch2_feed-stock-input',
        );
        for (let i = 0; i < ch2FeedCheckbox.length; i++) {
            ch2FeedCheckbox[i].checked = false;
            ch2FeedCheckbox[i].value = '';
        }
        $('#ch2_customizer-form').trigger('reset');

        $('.ch2_char-dropdown').dropdown('clear');
        $('.ch2_char-dropdown .icon').removeClass('clear');

        $('.ch2_color-dropdown').dropdown(
            'set selected',
            this.window.CH2Product.defaultColor,
        );
        $('.ch2_text-position-dropdown').dropdown('set selected', '0');

        for (
            let i = 0;
            i < this.productConfigurator.personalizedText.length;
            i++
        ) {
            this.productConfigurator.personalizedText[i] = '';
        }
        this.productConfigurator.runConfigurationFlow(
            this.productConfigurator.personalizedText.join('').trim(),
        );
    }

    /**
     * Function to destroy Playcanvas Application and HTML Elements generated.
     */
    destroy() {
        if (this.analytics) {
            this.analytics.logEvent('close_viewer', null, null);
            if (this.productConfigurator) {
                this.analytics.logBvlgariEvent('configurator_close', null, null);
            }
        }
        document.body.classList.remove('ch2_noscroll');

        $('.ch2_char-dropdown').dropdown('destroy').off('change');
        $('.ch2_color-dropdown').dropdown('destroy').off('change');
        $('.ch2_text-position-dropdown').dropdown('destroy').off('change');
        $('input:checkbox.ch2_feed-stock-input').off('change');
        $('.ch2_text-position-dropdown').dropdown('destroy').off('change');
        if (document.getElementById('ch2_configurator-reset')) {
            document.getElementById('ch2_configurator-reset').onclick = null;
        }

        if (this.productConfigurator) {
            this.configuratorReset();
            this.productConfigurator.destroy();
            this.productConfigurator = null;
        }
        if (this.buttonScreenshot) {
            this.buttonScreenshot.remove();
            this.buttonScreenshot = null;
        }
        if (this.buttonsContainer) {
            while (this.buttonsContainer.lastElementChild) {
                this.buttonsContainer.removeChild(
                    this.buttonsContainer.lastElementChild,
                );
            }
            this.buttonsContainer.remove();
            this.buttonsContainer = null;
        }
        if (this.instructionButton) {
            this.instructionButton.remove();
            this.instructionButton = null;
        }
        if (this.applicationParentContainer) {
            const isOpen = 'ch2_is-open';
            if (this.applicationParentContainer.classList.contains(isOpen)) {
                this.applicationParentContainer.classList.remove(isOpen);
            }
            const modeConfigFull = 'mode-configurator-full';
            if (
                this.applicationParentContainer.classList.contains(
                    modeConfigFull,
                )
            ) {
                this.applicationParentContainer.classList.remove(
                    modeConfigFull,
                );
            }
            const modeViewerSmall = 'mode-viewer-small';
            if (
                this.applicationParentContainer.classList.contains(
                    modeViewerSmall,
                )
            ) {
                this.applicationParentContainer.classList.remove(
                    modeViewerSmall,
                );
            }
            this.applicationParentContainer = null;
        }

        const ch2Show3dBtn = document.getElementById('ch2_show3d_btn');
        if (ch2Show3dBtn) {
            ch2Show3dBtn.classList.remove('ch2_active-btn');
        }

        const ch2Player3dModal = document.getElementById('ch2_player3d-modal');
        if (ch2Player3dModal) {
            ch2Player3dModal.setAttribute('class', 'mode-viewer-small');
        }

        if (this.app) {
            this.app.destroy();
            this.app = null;
        }
        if (this.applicationLoadingContainer) {
            this.applicationLoadingContainer.innerHTML = '';
            this.applicationLoadingContainer = null;
        }
        if (this.canvas) {
            this.canvas.remove();
        }
    }

    addToCart() {
        const cartObj = this.productConfigurator.addToCart();
        console.log('CH2Start - addToCart');

        // Triggero il normale add-to-cart ma passando come dati extra i dati del configuratore
        $('button.add-to-cart').trigger('click', {
            configurator: cartObj,
        });

        this.destroy();
    }
}

module.exports = CH2Start;
